import { useMemo, useState } from 'react';
import { AnalyticsKPICards } from '../AnalyticsKPICards';
import { InsightsViewSelector } from './InsightsViewSelector';
import { ENTITY_VIEW_OPTIONS, type EntityView } from './insightsViewOptions';
import { EntityTrendChart } from './EntityTrendChart';
import { AssetTypeFilter, type AssetFilterValue } from './AssetTypeFilter';
import { getDisabledAssetTypes } from './assetFilterUtils';
import { useGetAssetsQuery } from '../../api/emissions';
import { AssetType, type EmissionSummary, type IntensityMetrics } from '../../types/emissions';

interface TrendsDashboardProps {
  initiativeId: string;
  includeChildren?: boolean;
  summary?: EmissionSummary;
  previousSummary?: EmissionSummary;
  intensityMetrics?: IntensityMetrics;
  summaryLoading: boolean;
  previousLoading: boolean;
  intensityLoading: boolean;
  allTimePeriodStart?: string;
  allTimePeriodEnd?: string;
  availableYears?: number[];
  periodStart: string;
  periodEnd: string;
  selectedFiscalYear: number;
}

export const TrendsDashboard = ({
  initiativeId,
  includeChildren,
  summary,
  previousSummary,
  intensityMetrics,
  summaryLoading,
  previousLoading,
  intensityLoading,
  allTimePeriodStart,
  allTimePeriodEnd,
  periodStart,
  periodEnd,
}: TrendsDashboardProps) => {
  const [entityView, setEntityView] = useState<EntityView>('assets');
  const [selectedAssetType, setSelectedAssetType] = useState<AssetFilterValue>('all');
  const { data: assetsResponse } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren }, { skip: !initiativeId });
  const disabledAssetTypes = useMemo(() => getDisabledAssetTypes(assetsResponse?.data ?? []), [assetsResponse?.data]);

  return (
    <div className='tw:space-y-6'>
      {/* KPI Cards */}
      <AnalyticsKPICards
        initiativeId={initiativeId}
        summary={summary}
        previousSummary={previousSummary}
        intensityMetrics={intensityMetrics}
        isLoading={summaryLoading}
        previousLoading={previousLoading}
        intensityLoading={intensityLoading}
      />

      {/* Entity Trend Lines */}
      <InsightsViewSelector
        label='Trends by'
        options={ENTITY_VIEW_OPTIONS}
        value={entityView}
        onChange={setEntityView}
      />

      {entityView === 'assets' && (
        <AssetTypeFilter value={selectedAssetType} onChange={setSelectedAssetType} disabledTypes={disabledAssetTypes} />
      )}

      {entityView === 'reporting-levels' && (
        <EntityTrendChart
          initiativeId={initiativeId}
          periodStart={allTimePeriodStart ?? periodStart}
          periodEnd={allTimePeriodEnd ?? periodEnd}
          groupBy='initiative'
          includeChildren={includeChildren}
        />
      )}
      {entityView === 'assets' && (
        <EntityTrendChart
          initiativeId={initiativeId}
          periodStart={allTimePeriodStart ?? periodStart}
          periodEnd={allTimePeriodEnd ?? periodEnd}
          groupBy='asset'
          includeChildren={includeChildren}
          assetType={selectedAssetType === 'all' ? undefined : selectedAssetType as AssetType | 'unassigned'}
        />
      )}
    </div>
  );
};
